import { PublicKey } from "@solana/web3.js";
import {
  CreateClubWizardSteps,
  GovernanceConfigurationType,
} from "../../common/enums/clubs-wizard.enum";
import { usdcMint } from "../../solana/common/utilities";

import {
  EMPTY_STRING,
  MAX_NFT_VOTES,
  MAX_VOTING_POWER,
} from "../../common/constants/common.constants";
import {
  checkSolAmount,
  validateBasicClubInfo,
  validateSocialNetworks,
} from "../../utilities/helpers";
import {
  MAX_SOL_AMOUNT_PER_FUNDRAISE,
  MAX_USDC_AMOUNT_PER_FUNDRAISE,
  MIN_AMOUNT_PER_FUNDRAISE,
} from "../../common/constants/club.constants";
import {
  MAX_VOTING_DAYS,
  MIN_VOTING_DAYS,
} from "../../common/constants/clubs-wizard.constants";
import { NATIVE_MINT } from "@solana/spl-token";
import { AllocationModel } from "../../common/enums/fundraise.enum";

//TODO@milica@guta: add constants for all "magic numbers" and replace "" with EMPTY_STRING
const validateMembersStep = (values, errors) => {
  values.initialMembers.forEach((item, index) => {
    if (item.pubKey === "") {
      errors[`initialMembers.${index}.pubKey`] = "Public key is required field";
    } else {
      const membersWithSamePubKeysArray = values.initialMembers.filter(
        (member) => member.pubKey === item.pubKey
      );
      if (membersWithSamePubKeysArray.length > 1) {
        const indexOfLastElementWithPubKey = values.initialMembers
          .map((member) => member.pubKey)
          .lastIndexOf(item.pubKey);
        errors[`initialMembers.${indexOfLastElementWithPubKey}.pubKey`] =
          "You have already created member with this public key";
      } else {
        try {
          const publicKey = new PublicKey(item.pubKey);
          const isValid = PublicKey.isOnCurve(
            Uint8Array.from(publicKey.toBuffer())
          );
          if (!isValid) {
            errors[`initialMembers.${index}.pubKey`] =
              "Public key does not exist ";
          }
        } catch (error) {
          errors[`initialMembers.${index}.pubKey`] = "Public key is not valid";
        }
      }
    }
  });
};

export const validateFundraisingStep = (values, errors) => {
  if (values.skipFundraise) {
    return errors;
  }
  if (values.treasuryCap < MIN_AMOUNT_PER_FUNDRAISE) {
    errors.treasuryCap = `Treasury cap can not be less then ${MIN_AMOUNT_PER_FUNDRAISE}`;
  }

  if (
    values.allocationModel === AllocationModel.LIMITED &&
    !values?.memberLimitedAmount
  ) {
    errors.memberLimitedAmount = "Amount is required field";
  }
  if (
    values.mint.toString() === NATIVE_MINT.toString() &&
    values.treasuryCap > MAX_SOL_AMOUNT_PER_FUNDRAISE
  ) {
    errors.treasuryCap = "Treasury cap can not greater then 100000 SOL";
  }
  if (
    values.mint.toString() === usdcMint.toString() &&
    values.treasuryCap > MAX_USDC_AMOUNT_PER_FUNDRAISE
  ) {
    errors.treasuryCap = `Treasury cap can not greater then ${MAX_USDC_AMOUNT_PER_FUNDRAISE} USDC`;
  }

  if (values.treasuryCap) {
    checkSolAmount(values, errors, "treasuryCap");
  }

  if (values.raisedAmount && values.treasuryCap <= values.raisedAmount) {
    errors.treasuryCap =
      "Fundraise cap can not be less or equal to alredy deposited amount";
  }
};

const validateFinancialStep = (values, errors) => {
  if (values.votingQuorum === EMPTY_STRING) {
    errors.votingQuorum = "Voting quorum can't be empty.";
  }

  if (values.maxVoteTime === EMPTY_STRING) {
    errors.maxVoteTime = "Max voting time can't be empty.";
  }

  if (
    values.withdrawalVotingQuorum !== undefined &&
    values.withdrawalVotingQuorum !== EMPTY_STRING &&
    (values.withdrawalVotingQuorum < 51 || values.withdrawalVotingQuorum > 100)
  ) {
    errors.withdrawalVotingQuorum =
      "Approval vote percentage can't be less then 51 or greater then 100.";
  }

  if (
    values.transferVotingQuorum !== undefined &&
    values.transferVotingQuorum !== EMPTY_STRING &&
    values.transferVotingQuorum > 100
  ) {
    errors.transferVotingQuorum =
      "Approval vote percentage can't be greater then 100.";
  }

  if (
    values.withdrawalVotingQuorum !== undefined &&
    values.withdrawalVotingQuorum !== EMPTY_STRING &&
    (values.withdrawalVotingQuorum.toString().includes(".") ||
      values.withdrawalVotingQuorum.toString().includes(","))
  ) {
    errors.withdrawalVotingQuorum =
      "Approval vote percentage can not be decimal";
  }

  if (
    values.transferVotingQuorum !== undefined &&
    values.transferVotingQuorum !== EMPTY_STRING &&
    (values.transferVotingQuorum.toString().includes(".") ||
      values.transferVotingQuorum.toString().includes(","))
  ) {
    errors.transferVotingQuorum = "Approval vote percentage can not be decimal";
  }

  if (
    values.withdrawalMaxVoteTime !== undefined &&
    values.withdrawalMaxVoteTime !== EMPTY_STRING &&
    values.withdrawalMaxVoteTime < MIN_VOTING_DAYS
  ) {
    errors.withdrawalMaxVoteTime = "Max voting time can't be less then 7";
  }

  if (
    values.transferMaxVoteTime !== undefined &&
    values.transferMaxVoteTime !== EMPTY_STRING &&
    values.transferMaxVoteTime < MIN_VOTING_DAYS
  ) {
    errors.transferMaxVoteTime = "Max voting time can't be less then 7";
  }

  if (
    values.withdrawalMaxVoteTime !== undefined &&
    values.withdrawalMaxVoteTime !== EMPTY_STRING &&
    values.withdrawalMaxVoteTime > MAX_VOTING_DAYS
  ) {
    errors.withdrawalMaxVoteTime = "Max voting time can't be more than 255";
  }

  if (
    values.transferMaxVoteTime !== undefined &&
    values.transferMaxVoteTime !== EMPTY_STRING &&
    values.transferMaxVoteTime > MAX_VOTING_DAYS
  ) {
    errors.transferMaxVoteTime = "Max voting time can't be more than 255";
  }

  if (
    values.withdrawalMaxVoteTime !== undefined &&
    values.withdrawalMaxVoteTime !== EMPTY_STRING &&
    (values.withdrawalMaxVoteTime.toString().includes(".") ||
      values.withdrawalMaxVoteTime.toString().includes(","))
  ) {
    errors.withdrawalMaxVoteTime = "Max voting time can not be decimal";
  }

  if (
    values.transferMaxVoteTime !== undefined &&
    values.transferMaxVoteTime !== EMPTY_STRING &&
    (values.transferMaxVoteTime.toString().includes(".") ||
      values.transferMaxVoteTime.toString().includes(","))
  ) {
    errors.transferMaxVoteTime = "Max voting time can not be decimal";
  }
  if (
    values.maxVoteTime !== EMPTY_STRING &&
    values.maxVoteTime < MIN_VOTING_DAYS
  ) {
    errors.maxVoteTime = "Max voting time can't be less then 7";
  }

  if (
    values.maxVoteTime !== EMPTY_STRING &&
    values.maxVoteTime > MAX_VOTING_DAYS
  ) {
    errors.maxVoteTime = "Max voting time can't be more than 255";
  }

  if (
    values.maxVoteTime !== EMPTY_STRING &&
    (values.maxVoteTime.toString().includes(".") ||
      values.maxVoteTime.toString().includes(","))
  ) {
    errors.maxVoteTime = "Max voting time can not be decimal";
  }

  if (values.votingQuorum < 51 || values.votingQuorum > 100) {
    errors.votingQuorum =
      "Approval vote percentage can't be less than 51 or greater than 100.";
  }

  if (
    values.votingQuorum !== EMPTY_STRING &&
    (values.votingQuorum.toString().includes(".") ||
      values.votingQuorum.toString().includes(","))
  ) {
    errors.votingQuorum = "Approval vote percentage can not be decimal";
  }

  values.votingThreshold.map((configuration, index) => {
    if (configuration.from < 0 || configuration.from === "") {
      errors[`votingThreshold.${index}.from`] =
        "Value from can't be empty or less than zero.";
    }
    if (configuration.to < 0 || configuration.to === "") {
      errors[`votingThreshold.${index}.to`] =
        "Value to can't be empty or less than zero.";
    }
    if (configuration.approvalQuorum === "") {
      errors[`votingThreshold.${index}.approvalQuorum`] =
        "Value at least can't be empty";
    }

    if (
      configuration.from &&
      configuration.from !== "" &&
      configuration.to &&
      configuration.to !== "" &&
      configuration.from >= configuration.to
    ) {
      errors[`votingThreshold.${index}.from`] =
        "Value from has to be less than value to.";
    }

    if (
      configuration.approvalQuorum &&
      configuration.approvalQuorum !== "" &&
      (configuration.approvalQuorum.toString().includes(".") ||
        configuration.approvalQuorum.toString().includes(","))
    ) {
      errors[`votingThreshold.${index}.approvalQuorum`] =
        "Value can not be decimal";
    }

    if (
      configuration.approvalQuorum &&
      configuration.approvalQuorum !== "" &&
      (configuration.approvalQuorum < 0 || configuration.approvalQuorum > 100)
    ) {
      errors[`votingThreshold.${index}.approvalQuorum`] =
        "Value at least can't be less then 0 or greater then 100";
    }

    if (
      configuration.from &&
      configuration.from !== EMPTY_STRING &&
      configuration.to &&
      configuration.to !== EMPTY_STRING &&
      values.votingThreshold.find(
        (item, itemIndex) =>
          index !== itemIndex &&
          item.from &&
          item.from !== EMPTY_STRING &&
          item.to &&
          item.to !== EMPTY_STRING &&
          configuration.from < item.to &&
          configuration.to > item.from
      )
    ) {
      errors[`votingThreshold.${index}.from`] =
        "You have already set values for this range";
      errors[`votingThreshold.${index}.to`] =
        "You have already set values for this range";
      return errors;
    }
  });
};

export const validateClubConfigurationStep = async (values, errors) => {
  if (values.governance === GovernanceConfigurationType.NFT) {
    if (
      values.totalNftVotes < 0 ||
      values.totalNftVotes === null ||
      values.totalNftVotes === EMPTY_STRING
    ) {
      errors.totalNftVotes = "Nft votes can not be empty or negative!";
    }
  }
  return errors;
};
const validateRoleConfiguration = (values, errors) => {
  if (values.governance === "role-based") {
    if (
      values.curator &&
      (values.curatorVotingPower > MAX_VOTING_POWER ||
        values.curatorVotingPower < 1)
    ) {
      errors.curatorVotingPower =
        "Number of votes can not be bigger than 1 milion or less than 1";
    }

    if (
      values.ownerVotingPower > MAX_VOTING_POWER ||
      values.ownerVotingPower < 1
    ) {
      errors.ownerVotingPower =
        "Number of votes can not be bigger than 1 milion or less than 1";
    }
    if (
      values.memberVotingPower > MAX_VOTING_POWER ||
      values.memberVotingPower < 1
    ) {
      errors.memberVotingPower =
        "Number of votes can not be bigger than 1 milion or less than 1";
    }
    if (values.ownerVotingPower === EMPTY_STRING) {
      errors.ownerVotingPower = "Votes can not be empty.";
    }
    if (values.memberVotingPower === EMPTY_STRING) {
      errors.memberVotingPower = "Votes can not be empty.";
    }
    if (values.curator && values.curatorVotingPower === EMPTY_STRING) {
      errors.curatorVotingPower = "Votes can not be empty.";
    }
  }
  return errors;
};

export const validateClubCreationForm = async (values, activeStep) => {
  const errors: any = {};
  switch (activeStep) {
    case CreateClubWizardSteps.BasicInfo:
      validateBasicClubInfo(errors, values);
      validateSocialNetworks(errors, values);
      return errors;
    case CreateClubWizardSteps.Roles:
      validateRoleConfiguration(values, errors);
      return errors;
    case CreateClubWizardSteps.Members:
      validateMembersStep(values, errors);
      return errors;
    case CreateClubWizardSteps.NFTConfiguration:
      if (values.governance === GovernanceConfigurationType.NFT) {
        if (values.collectionMintAddress === EMPTY_STRING) {
          errors.collectionMintAddress = "Collection mint cannot be empty!";
        }
        if (
          values.collectionMintAddress !== EMPTY_STRING &&
          !values.isCollectionMintValid
        ) {
          errors.collectionMintAddress =
            "Collection data not found or collection is not verified!";
        }
        if (
          values.totalNftVotes <= 0 ||
          values.totalNftVotes === null ||
          values.totalNftVotes === EMPTY_STRING
        ) {
          errors.totalNftVotes =
            "Total votes can not be empty or negative number!";
        }
        if (values.totalNftVotes > MAX_NFT_VOTES || values.totalNftVotes < 1) {
          errors.totalNftVotes =
            "Total NFT votes cannot be bigger than 1 million or less than 1";
        }
      }
      return errors;
    case CreateClubWizardSteps.FundraisingConfiguration:
      validateFundraisingStep(values, errors);
      return errors;
    case CreateClubWizardSteps.FinancialConfiguration:
      validateFinancialStep(values, errors);
      return errors;
    case CreateClubWizardSteps.Preview:
      if (values.termsConditions === false) {
        errors.termsConditions = "Terms & conditions is not checked!";
      }

      return errors;
    default:
      return errors;
  }
};
